import { Badge, Button, Card, Collapse, SimpleGrid, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconChevronUp, IconSparkles } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  resolveChecklistColor,
  resolveChecklistIcon,
} from 'src/Components/Checklists/checklistVisuals.helper';
import { ChecklistTemplateAudience } from 'src/types/checklist-template.types';

import styles from './ChecklistTemplatesSection.module.scss';
import { Props } from './ChecklistTemplatesSection.types';

const AUDIENCE_LABELS: Record<ChecklistTemplateAudience, string> = {
  ALL: 'Tous',
  PROVIDER: 'Prestataire',
  OCCUPANTS: 'Famille & locataires',
};

const ChecklistTemplatesSection = ({ templates, disabled, onUseAsIs, onCustomize }: Props) => {
  const { t } = useTranslation();
  const [opened, { toggle }] = useDisclosure(true);
  const [expandedTemplateKey, setExpandedTemplateKey] = useState<string | undefined>(undefined);

  if (!templates || templates.length === 0) return null;

  return (
    <div className={styles.container}>
      <UnstyledButton onClick={toggle} className={styles.header}>
        <IconSparkles size={20} className={styles.headerIcon} />
        <div className={styles.headerTitle}>
          {t('checklist.templates.title', 'Modèles ShareHeritage')}
        </div>
        {opened ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
      </UnstyledButton>
      <Collapse in={opened}>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} className={styles.grid}>
          {templates.map(template => {
            const Icon = resolveChecklistIcon(template.icon);
            const color = resolveChecklistColor(template.color);
            const isExpanded = expandedTemplateKey === template.key;

            return (
              <Card key={template.key} withBorder className={styles.card}>
                <div className={styles.cardTopLine}>
                  <div className={styles.iconBadge} style={{ backgroundColor: `${color}1f` }}>
                    <Icon size={20} color={color} />
                  </div>
                  <Badge variant="outline" color="gray" size="sm">
                    {AUDIENCE_LABELS[template.audience]}
                  </Badge>
                </div>
                <div className={styles.cardTitle}>{template.title}</div>
                <div className={styles.cardStepsCount}>
                  {t('checklist.templates.itemsCount', '{{ count }} étapes', {
                    count: template.itemsCount,
                  })}
                </div>
                <div className={styles.cardDescription}>{template.description}</div>
                <UnstyledButton
                  onClick={() => setExpandedTemplateKey(isExpanded ? undefined : template.key)}
                  className={styles.previewToggle}
                >
                  {isExpanded
                    ? t('checklist.templates.hidePreview', 'Masquer les étapes')
                    : t('checklist.templates.showPreview', 'Voir les étapes')}
                </UnstyledButton>
                <Collapse in={isExpanded}>
                  <ul className={styles.itemsList}>
                    {template.items.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </Collapse>
                <div className={styles.cardActions}>
                  <Button
                    variant="outline"
                    disabled={disabled}
                    onClick={() => onCustomize(template)}
                  >
                    {t('checklist.templates.customize', 'Personnaliser')}
                  </Button>
                  <Button variant="primary" disabled={disabled} onClick={() => onUseAsIs(template)}>
                    {t('checklist.templates.useAsIs', 'Utiliser tel quel')}
                  </Button>
                </div>
              </Card>
            );
          })}
        </SimpleGrid>
      </Collapse>
    </div>
  );
};

export default ChecklistTemplatesSection;
